import { useContext, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
    GetTransactionsRequest,
    REPORT_TYPE_DETAILS,
    TransactionsReportType,
    useTransactions
} from "../api/transactions";
import { DiscountsSummary, Surcharge, TransactionSummary } from "../models/transactions";
import objectArrayToCSV from "./objectArrayToCSV";
import { hasFeature, hasModule } from "./helpers";
import { UserContext } from "../context/UserContext";
import getPrintableSummary from "../screens/transactions/print/printTransactionSummary";

export type ExportFormats = "csv" | "pdf";

export interface BulkExportParams extends GetTransactionsRequest {
    filename?: string;
    reportType: TransactionsReportType;
    isAdmin?: boolean;
    format?: ExportFormats;
}

const BULK_EXPORT_RATE = 500;

const useBulkExport = (params: BulkExportParams) => {

    const { filename, reportType, isAdmin, format = "csv" } = params;

    const [fileUri, setFileUri] = useState<string>();
    const [error, setError] = useState<Error>();
    const [page, setPage] = useState(1);
    const [records, setRecords] = useState<TransactionSummary[]>([]);
    const [progress, setProgress] = useState<number>();

    const { userModules, labsFeatures, locale } = useContext(UserContext);
    const { t } = useTranslation();

    const hasModuleInvoiceNumber = useMemo(
        () => hasModule(userModules, "ModuleInvoiceNumbers"),
        [userModules]
    );

    const hasModuleTicketNumber = useMemo(
        () => hasModule(userModules, "ModuleTicketNumber"),
        [userModules]
    );

    const hasPspReferencesNeededModules = useMemo(
        () =>
            hasModule(userModules, "_EposNowBalancePlatform") ||
            hasModule(userModules, "_EposNowPayments"),
        [userModules]
    );

    const hasFeatureCCSurcharge = useMemo(
        () => hasFeature(labsFeatures, "CCSurcharge"),
        [labsFeatures]
    );

    useEffect(() => {
        if (!filename) return;

        setPage(1);
        setRecords([]);
        setProgress(undefined);
        setFileUri(undefined);
        setError(undefined);
    }, [filename]);

    useEffect(() => {
        if (reportType === REPORT_TYPE_DETAILS && format === "csv") {
            setError(new Error("Details export not implemented for CSV"));
        }
    }, [reportType, format]);

    const transformer = (key: string, val: unknown) => {
        if (key === "discountsSummary") return (val as DiscountsSummary).totalDiscountAmount;
        if (key === "invoiceNumbers") return (val as string[])?.join(",");
        if (key === "pspReferences" && hasPspReferencesNeededModules)
            return (val as string[])?.join(",");
        if (key === "surcharge" && hasFeatureCCSurcharge)
            return (val as Surcharge)?.total;

        return val;
    };

    const genCSVBlob = <T>(rows: T[]): string => {

        const excludeColumns: (keyof T)[] = ["paymentLogo" as keyof T];

        if (!hasModuleTicketNumber) excludeColumns.push("ticketNumber" as keyof T);
        if (!hasModuleInvoiceNumber) excludeColumns.push("invoiceNumbers" as keyof T);
        if (!hasPspReferencesNeededModules) excludeColumns.push("pspReferences" as keyof T);
        if (!hasFeatureCCSurcharge) excludeColumns.push("surcharge" as keyof T);

        const blob = new Blob(
            [objectArrayToCSV(rows, ",", "/", excludeColumns, transformer)],
            { type: "application/octet-stream" }
        );

        return URL.createObjectURL(blob);
    };

    const genPDFBlob = (rows: TransactionSummary[]) => {

        const pdf = getPrintableSummary(rows, {
            translate: t,
            locale,
            hasModuleInvoiceNumber,
            hasModuleTicketNumber,
            hasPspReferencesNeededModules,
            hasFeatureCCSurcharge
        });

        if (!pdf) {
            setError(new Error("PDF generation failed"));
            return undefined;
        }
        const pdfBlob = pdf.output("blob");
        console.log("PDF blob:", pdfBlob);
        console.log("Object URL:", URL.createObjectURL(pdfBlob));

        return URL.createObjectURL(
            new Blob([pdfBlob], { type: "application/pdf" })
        );
    };

    const genFile = (rows: TransactionSummary[]) =>
        format === "csv" ? genCSVBlob(rows) : genPDFBlob(rows);

    const { data, isError } = useTransactions<TransactionSummary>(
        {
            ...params,
            page,
            limit: BULK_EXPORT_RATE
        },
        `${filename}-${page}`,
        {
            enabled: !!filename && reportType === "summary"
        },
        isAdmin
    );

    useEffect(() => {

        if (!data || !filename) return;

        const paginfo = data.pagination;

        setRecords(prevRecords => {

            const allRecords = [...prevRecords, ...data.transaction];

            if (paginfo.totalPages && page >= paginfo.totalPages) {

                setProgress(100);
                setFileUri(genFile(allRecords));

            } else {

                setPage(prev => prev + 1);

                if (paginfo.totalPages) {
                    setProgress(Math.ceil((page / paginfo.totalPages) * 100));
                }

            }

            return allRecords;
        });

    }, [data, filename, page]);

    useEffect(() => {
        if (isError) {
            setError(new Error("Fetch failed"));
        }

    }, [isError]);

    return {
        progress,
        fileUri,
        error
    };
};

export default useBulkExport;
